import Input, { InputWithErrorDescription } from 'components/base/Input';
import MenuButton, { MenuButtonItem } from 'components/MenuButton';
import { SaveButton, SaveAsButton } from '../views/CustomReport/SaveButtons';
import { Tooltip } from '@material-ui/core';
import Text from 'components/base/Text';
import { Flex, Padding } from 'components/base/layout-components';
import { getColor } from 'common/constants/_colors';
import styled from 'styled-components';
import { SPACES } from 'common/constants/sizes';
import Icon from 'components/base/Icon';
import UpgradeTooltip from '../views/CustomReport/UpgradeTooltip';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import * as LighthouseService from 'common/services/lighthouse.service';
import { LBButton } from '@linearb/buzz';

const TextWithBorder = styled(Text)`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  ${({ pr }) =>
    pr ? `padding-right: ${SPACES[pr]};` : `padding-right: ${SPACES.small};`}
`;

const StyledIcon = styled(Icon)`
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;

  :hover {
    cursor: pointer;
    color: ${getColor('chambray')} !important;
  }
`;

const DescriptionToolTip = ({ title, description }) => {
  return (
    <Flex direction='column' padding='small' width='23rem'>
      <Text size='large' weight='500'>
        {title}
      </Text>
      <Text mt='1rem' color='shuttleGray' lineHeight='1.5' size='large'>
        {description}
      </Text>
    </Flex>
  );
};

export const TitleSection = ({
  title,
  articleId,
  isOnEditMode,
  setEditedName,
  setEditMode,
  isEditable,
  description,
  isDashboardNameValid
}) => {
  return isOnEditMode ? (
    <Flex mr='small' width='100%'>
      <InputWithErrorDescription
        height='3rem'
        width='100%'
        flex='1'
        value={title}
        onChange={name => setEditedName({ name })}
        isError={!isDashboardNameValid}
        errorDescription='This name already exists'
      />
    </Flex>
  ) : (
    <Flex items='center' grow='1'>
      <TextWithBorder
        size='x-x-large'
        {...(isEditable
          ? { onClick: () => setEditMode(true), pr: SPACES['small'] }
          : {})}
      >
        {title}
      </TextWithBorder>
      {articleId && (
        <Padding r='small'>
          <LBButton
            variant='outline'
            size='icon'
            onClick={() => LighthouseService.navigateArticle(articleId)}
          >
            <Icon name='helpicon' size='smaller' />
          </LBButton>
        </Padding>
      )}
      {isEditable && description && (
        <Tooltip
          placement='bottom'
          title={<DescriptionToolTip title={title} description={description} />}
        >
          <StyledIcon name='infooutlined' size='medium' color='nepal' />
        </Tooltip>
      )}
    </Flex>
  );
};

export const SaveSection = ({
  isCreator,
  reportName,
  saveChanges,
  isNew,
  isDashboardNameValid,
  validateName
}) => {
  return (
    <>
      {!isNew && (
        <SaveAsButton
          text={isCreator ? 'Save As' : 'Save'}
          {...(isCreator ? {} : { variant: 'default' })}
          reportName={reportName}
          saveChanges={saveChanges}
          isDashboardNameValid={isDashboardNameValid}
          validateName={validateName}
        />
      )}
      {(isCreator || isNew) && (
        <SaveButton
          isNew={isNew}
          reportName={reportName}
          saveChanges={saveChanges}
          isDashboardNameValid={isDashboardNameValid}
        />
      )}
    </>
  );
};

export const EditDashboardSection = ({
  addMetrics,
  deleteDashboard,
  setEditMode,
  isOnEditMode,
  isCreator,
  isAdmin,
  autoCreated = false
}) => {
  return (
    <MenuButton
      width='20rem'
      padding='x-x-x-small'
      buttonText={'More'}
      buttonVariant='outline'
      items={[
        !isOnEditMode ? (
          <MenuButtonItem
            key='Edit dashboard'
            text='Edit dashboard'
            icon={<Icon name='pencil' color='nepal' size='smaller' />}
            onClick={() => {
              TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                page: TRACKING.PAGES.METRICS,
                click_type: TRACKING.CLICK.EDIT_DASHBOARD
              });
              setEditMode(true);
            }}
          />
        ) : null,
        isOnEditMode ? (
          <MenuButtonItem
            key='Add metric'
            text='Add metric'
            icon={<Icon name='add' color='nepal' />}
            onClick={addMetrics}
          />
        ) : null,
        !autoCreated && (isCreator || isAdmin) ? (
          <MenuButtonItem
            key='Delete Dashboard'
            text='Delete Dashboard'
            data-cy='delete-dashboard'
            icon={<Icon name='Trash' color='nepal' size='smaller' />}
            onClick={deleteDashboard}
          />
        ) : null
      ]}
    />
  );
};

export const EditMode = ({
  reportDescription,
  updateValues,
  isTeamLicense,
  isPublic,
  togglePrivacy,
  isDashboardNameValid
}) => {
  return (
    <Flex mt={!isDashboardNameValid && 'small'} items='center' gap='1rem'>
      <Input
        placeholder='Description'
        value={reportDescription}
        onChange={description => updateValues({ description })}
        height='3rem'
        width='100%'
      />
      <Tooltip
        interactive
        placement='top'
        title={
          isTeamLicense ? (
            <UpgradeTooltip
              title='Upgrade to make this dashboard private'
              text={[
                'On the free plan all dashboards are public - visible to everyone at your company.',
                'Upgrade your plan to enjoy private custom dashboards and more.'
              ]}
            />
          ) : (
            <Flex items='center'>
              <Icon
                name={isPublic ? 'LockClosed' : 'LockOpen'}
                color='nepal'
                size='smaller'
              />
              <Text ml='0.5rem'>
                make this a {isPublic ? 'private' : 'public'} dashboard
              </Text>
            </Flex>
          )
        }
      >
        <LBButton
          disabled={isTeamLicense}
          variant='outline'
          onClick={togglePrivacy}
          startIcon={
            <Icon name={isPublic ? 'LockOpen' : 'LockClosed'} color='nepal' />
          }
        >
          {isPublic ? 'Public' : 'Private'}
        </LBButton>
      </Tooltip>
    </Flex>
  );
};
